import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import type { CashflowAccount, CreateAccountPayload, PocketType, UpdateAccountPayload } from '@/types/cashflow';
import { POCKET_TYPES } from '@/utils/cashflowUtils';

interface CashflowAddAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateAccountPayload | UpdateAccountPayload) => Promise<void>;
  onDelete?: () => void;
  accountToEdit?: CashflowAccount | null;
  isLoading?: boolean;
}

export const CashflowAddAccountModal: React.FC<CashflowAddAccountModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  accountToEdit,
  isLoading,
}) => {
  const [name, setName] = useState('');
  const [pocketType, setPocketType] = useState<PocketType>('rekening');
  const [initialBalance, setInitialBalance] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (accountToEdit) {
      if (accountToEdit.is_sporto_wallet || accountToEdit.is_kyc_wallet) {
        onClose();
        return;
      }
      setName(accountToEdit.name || '');
      setPocketType(accountToEdit.pocket_type || 'rekening');
      setInitialBalance(accountToEdit.initial_balance || 0);
      setNotes(accountToEdit.notes || '');
    } else {
      setName('');
      setPocketType('rekening');
      setInitialBalance('');
      setNotes('');
    }
    setError(null);
  }, [accountToEdit, isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Nama kantong kas wajib diisi');
      return;
    }

    try {
      if (accountToEdit) {
        await onSubmit({
          name: name.trim(),
          pocket_type: accountToEdit.is_sporto_wallet ? (accountToEdit.pocket_type || 'sporto_wallet') : pocketType,
          notes: notes.trim() || undefined,
        });
      } else {
        await onSubmit({
          name: name.trim(),
          pocket_type: pocketType,
          initial_balance: typeof initialBalance === 'number' ? initialBalance : 0,
          notes: notes.trim() || undefined,
        });
      }
      onClose();
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Gagal menyimpan kantong kas');
      }
    }
  };

  const selectableTypes: PocketType[] = ['rekening', 'kas_tunai', 'ewallet', 'lainnya'];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-xs transition-opacity"
        onClick={onClose}
      />

      {/* Modal Dialog */}
      <div className="relative w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 shadow-2xl z-10 space-y-4 max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-200">
        <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto sm:hidden" />
        <div className="flex items-center justify-between pb-2 border-b border-[#E0E6ED]">
          <div>
            <h3 className="text-base font-extrabold text-[#0E1726]">
              {accountToEdit ? 'Edit Kantong Kas' : 'Tambah Posisi Dana'}
            </h3>
            <p className="text-xs text-gray-400 font-medium">
              {accountToEdit ? 'Perbarui informasi kantong kas' : 'Catat rekening atau kas komunitas kamu'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icon icon="ph:x-bold" className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="p-3 rounded-2xl bg-rose-50 border border-rose-100 text-xs font-semibold text-rose-600">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 1. Nama Kantong */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">
              Nama Kantong <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              required
              placeholder="Contoh: Rekening BCA, Kas Tunai"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FC480D]/20 focus:border-[#FC480D] text-sm text-[#0E1726] font-medium"
            />
          </div>

          {/* 2. Jenis Kantong (Chips) */}
          {!accountToEdit?.is_sporto_wallet ? (
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">
                Jenis Kantong
              </label>
              <div className="grid grid-cols-2 gap-2">
                {selectableTypes.map((typeKey) => {
                  const meta = POCKET_TYPES[typeKey];
                  const isSelected = pocketType === typeKey;
                  return (
                    <button
                      key={typeKey}
                      type="button"
                      onClick={() => setPocketType(typeKey)}
                      className={`p-2.5 rounded-2xl border text-xs font-bold flex items-center gap-2 transition-all cursor-pointer ${
                        isSelected
                          ? 'border-[#FC480D] bg-orange-50/50 text-[#FC480D] shadow-2xs'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon icon={meta.icon} className="w-4 h-4 shrink-0" />
                      <span className="truncate">{meta.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="p-3.5 rounded-2xl bg-orange-50/70 border border-orange-100/80 text-xs text-orange-950 space-y-1">
              <span className="font-bold flex items-center gap-1.5 text-[#FC480D]">
                <Icon icon="ph:wallet-bold" className="w-4 h-4" />
                Dompet Sporto
              </span>
              <p className="text-gray-600 text-[11px] leading-relaxed">
                Kantong kas otomatis untuk transaksi event via QRIS. Anda dapat menyesuaikan nama dan catatan kantong ini.
              </p>
            </div>
          )}

          {/* 3. Saldo Awal (only on create) */}
          {!accountToEdit && (
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">
                Saldo Awal (Rp)
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-2.5 text-sm font-bold text-gray-400">
                  Rp
                </span>
                <input
                  type="number"
                  min="0"
                  placeholder="0"
                  value={initialBalance === '' ? '' : initialBalance}
                  onChange={(e) => {
                    const val = e.target.value;
                    setInitialBalance(val === '' ? '' : Number(val));
                  }}
                  className="w-full pl-10 pr-3.5 py-2.5 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FC480D]/20 focus:border-[#FC480D] text-sm text-[#0E1726] font-bold"
                />
              </div>
              <p className="text-[11px] text-gray-400 mt-1">
                Saldo awal saat kamu mulai mencatat kantong ini.
              </p>
            </div>
          )}

          {/* 4. Catatan / Keterangan */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">
              Catatan (Opsional)
            </label>
            <textarea
              rows={2}
              placeholder="Nomor rekening atau keterangan lainnya"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FC480D]/20 focus:border-[#FC480D] text-sm text-[#0E1726] font-medium resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2">
            {accountToEdit && onDelete && !accountToEdit.is_sporto_wallet && !accountToEdit.is_kyc_wallet && (
              <button
                type="button"
                onClick={() => {
                  onDelete();
                  onClose();
                }}
                className="py-2.5 px-3.5 rounded-2xl border border-red-200 text-xs font-bold text-[#EF4444] hover:bg-red-50 active:scale-95 transition-all flex items-center justify-center gap-1.5 cursor-pointer shrink-0"
                title="Hapus Kantong Kas"
              >
                <Icon icon="ph:trash-bold" className="w-3.5 h-3.5" />
                <span>Hapus</span>
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 px-4 rounded-2xl border border-gray-200 text-xs font-bold text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 py-2.5 px-4 rounded-2xl bg-[#FC480D] text-white text-xs font-bold hover:bg-[#e03e08] transition-colors shadow-2xs disabled:opacity-50 cursor-pointer"
            >
              {isLoading ? 'Menyimpan...' : 'Simpan Kantong'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
